import { useCallback, useEffect, useMemo, useState } from 'react'
import { FormattedMessage } from 'react-intl'
import { Button, Dialog, DialogContent, DialogFooter, DialogHeader } from '@carto/meridian-ds/components'
import { App } from 'features/apps/types'
import PrivacySettingsRadioGroup from 'features/common/ui/sharing/PrivacySettingsRadioGroup'
import { Group } from 'features/groups'
import { CommonPrivacy, CommonSharingScope, ResourceType } from 'features/common'
import SharedWithOrganizationMode from 'features/common/ui/sharing/SharedWithOrganizationMode/SharedWithOrganizationMode'
import { PrivatePrivacy, SharedPrivacy } from 'features/common/ui/sharing/SharingOptions'
import { SharedOptionDescription } from 'features/common/ui/sharing/ShareOptionShared/ShareOptionShared'
import { useGroupsByIds } from 'features/groups/hooks/groupsHooks'
import { SharingProvider } from 'features/common/ui/sharing/SharingContext'

export type AppSharingOptions = Pick<App, 'privacy' | 'groups' | 'sharingScope'>

function AppsShareDialogUiWrapper(props) {
  return (
    <SharingProvider>
      <AppsShareDialogUi {...props} />
    </SharingProvider>
  )
}

function AppsShareDialogUi({
  open,
  onClose,
  app,
  onChange,
  isUpdating = false
}: {
  open: boolean
  onClose: () => void
  app: Pick<App, 'id'> & AppSharingOptions
  onChange: (sharingUpdate: Partial<AppSharingOptions>) => void
  isUpdating?: boolean
}) {
  const [privacy, setPrivacy] = useState<CommonPrivacy>(app.privacy || CommonPrivacy.Shared)
  const [mode, setMode] = useState<string>(
    app.groups || app.sharingScope === CommonSharingScope.Specific ? 'groups' : 'organization'
  )
  const { data: sharedGroups } = useGroupsByIds(app.groups as string[])
  const [groups, setGroups] = useState<Group[]>(sharedGroups || [])

  useEffect(() => {
    if (app) {
      setPrivacy(app.privacy ?? CommonPrivacy.Shared)
      setGroups(sharedGroups ?? [])
      setMode(app.groups?.length ? 'groups' : 'organization')
    }
  }, [app, sharedGroups])

  const update = useCallback(
    (update: Partial<AppSharingOptions> & { groups?: Group[] }) => {
      if (update.privacy !== undefined) {
        setPrivacy(update.privacy)
      }
      if (update.groups !== undefined) {
        setGroups(update.groups)
      }

      onChange(update)
    },
    [onChange]
  )

  const handleChangeShareOption = useCallback(
    (item) => {
      const sharingScope =
        item.id === CommonPrivacy.Shared
          ? app.sharingScope === CommonSharingScope.Specific
            ? CommonSharingScope.Specific
            : CommonSharingScope.Organization
          : undefined
      update({ privacy: item.id, sharingScope })
    },
    [update, app.sharingScope]
  )

  const handleOnSharingOrganizationMode = useCallback(
    (mode) => {
      if (mode === 'organization') {
        update({ privacy: CommonPrivacy.Shared, groups: [], sharingScope: CommonSharingScope.Organization })
      }
      setMode(mode)
    },
    [update]
  )

  const handleOnChooseGroups = useCallback(
    (groups) => {
      update({ privacy: CommonPrivacy.Shared, groups, sharingScope: CommonSharingScope.Specific })
      setGroups(groups)
    },
    [update]
  )

  const description = useMemo(() => {
    if (privacy === CommonPrivacy.Shared) {
      const extraText = mode === 'viewer' && <span>(requires credentials)</span>
      return <SharedOptionDescription groups={groups} extraText={extraText} />
    }
    return
  }, [privacy, mode, groups])

  if (!open) {
    return null
  }

  return (
    <Dialog open={open} size="medium" height="auto" onClose={onClose}>
      <DialogHeader title={<FormattedMessage id="common.shareDialog.title" />} onClose={onClose} />
      <DialogContent>
        <PrivacySettingsRadioGroup
          value={privacy}
          onChange={handleChangeShareOption}
          options={[PrivatePrivacy, SharedPrivacy]}
          description={description}
        />
        {privacy === CommonPrivacy.Shared && (
          <SharedWithOrganizationMode
            mode={mode}
            sharingScope={app.sharingScope}
            groups={groups}
            onChangeMode={handleOnSharingOrganizationMode}
            onChangeGroups={handleOnChooseGroups}
            resourceType={ResourceType.App}
          />
        )}
      </DialogContent>
      <DialogFooter>
        <Button onClick={onClose} loading={!!isUpdating}>
          <FormattedMessage id="common.button.done" />
        </Button>
      </DialogFooter>
    </Dialog>
  )
}

export default AppsShareDialogUiWrapper
